import { ReactNode, useMemo } from 'react';
import { UsersContext } from './UsersContext';
import { UsersHostProps } from './types';
import useUsersList from './useUsersList';

type UsersProviderProps = Omit<UsersHostProps, 'title' | 'titleLevel'> & {
  children: ReactNode;
};

// Feature-local, not app-level: the Users root mounts this with the props the host passed it, so the
// feature's internal components read useUsers() instead of being handed viewer/account/permissions
// one level at a time. The user list is fetched here too: the table renders it, and the role drawers
// read a listed user off it when the host sources the permission catalog from the account.
// Deliberately not exported from the package entry point.
export default function UsersProvider({
  viewer,
  account,
  userPermissions,
  tableContainer,
  permissionCatalogSource,
  createRoleInSubAccounts,
  updateRoleInSubAccounts,
  countSubAccounts,
  deleteRoleFromSubAccounts,
  children,
}: UsersProviderProps) {
  const { users, fetchingUsers, fetchUsers } = useUsersList({ timezone: viewer.timezone, bearer: account.bearer });

  const value = useMemo(
    () => ({
      viewer,
      account,
      userPermissions,
      tableContainer,
      permissionCatalogSource,
      createRoleInSubAccounts,
      updateRoleInSubAccounts,
      countSubAccounts,
      deleteRoleFromSubAccounts,
      users,
      fetchingUsers,
      fetchUsers,
    }),
    [
      viewer,
      account,
      userPermissions,
      tableContainer,
      permissionCatalogSource,
      createRoleInSubAccounts,
      updateRoleInSubAccounts,
      countSubAccounts,
      deleteRoleFromSubAccounts,
      users,
      fetchingUsers,
      fetchUsers,
    ],
  );

  return <UsersContext.Provider value={value}>{children}</UsersContext.Provider>;
}
